export interface PmSummaryDto {
  totalIssues: number;
  openIssues: number;
  inProgressIssues: number;
  doneIssues: number;
  unassignedIssues: number;
  activeSprints: number;
  unreleasedVersions: number;
  completionPercent?: number;
}

export interface ChartPointDto { label: string; value: number; }

export interface BurndownReportDto {
  sprintId: string;
  sprintName: string;
  startDate: string;
  endDate: string;
  totalPoints: number;
  idealLine: ChartPointDto[];
  remainingLine: ChartPointDto[];
}

export interface BurnupReportDto {
  sprintId: string;
  sprintName: string;
  startDate: string;
  endDate: string;
  scopePoints: number;
  scopeLine: ChartPointDto[];
  completedLine: ChartPointDto[];
}

export interface VelocitySprintDto {
  sprintId: string;
  name: string;
  committedPoints: number;
  completedPoints: number;
}

export interface VelocityReportDto {
  projectId: string;
  sprints: VelocitySprintDto[];
  averageCompleted: number;
}

export interface TeamWorkloadDto {
  employeeId?: string;
  employeeName: string;
  openCount: number;
  inProgressCount: number;
  doneCount: number;
  totalStoryPoints: number;
  remainingStoryPoints: number;
}

export interface DeadlineDashboardDto {
  dueToday: number;
  dueTomorrow: number;
  dueThisWeek?: number;
  overdue: number;
  completedToday: number;
  delayed: number;
  upcomingDeadlines: number;
  averageDelayDays: number;
  overallHealth: number;
  overallHealthLabel: string;
  blocked?: number;
  atRisk?: number;
  critical?: number;
  teamWorkload: { employeeId?: string; employeeName: string; openCount: number; overdueCount: number; dueSoonCount: number }[];
}

export interface ProjectDeadlineHealthDto {
  projectId: string;
  projectName: string;
  totalTasks: number;
  completed: number;
  pending: number;
  overdue: number;
  blocked: number;
  delayed: number;
  completionPercent: number;
  averageDelayDays: number;
  health: number;
  healthLabel: string;
  healthScore?: number;
  riskScore?: number;
  overduePercent?: number;
  blockedPercent?: number;
  velocityTrend?: number | string;
  upcomingDeadlines: DeadlineReportRowDto[];
}

export interface DeadlineReportRowDto {
  id: string;
  keyOrTitle: string;
  projectId: string;
  projectName: string;
  workItemType: string;
  statusLabel: string;
  assigneeId?: string;
  assigneeName?: string;
  dueDate?: string;
  isOverdue: boolean;
  delayInDays: number;
  delayLabel: string;
  remainingLabel: string;
  deadlineBadge?: string;
  escalationLevel?: number;
  escalationLabel?: string;
  actualCompletionDate?: string;
}

export interface EmployeeDelayPerformanceDto {
  employeeId: string;
  employeeName: string;
  completedBeforeDeadline: number;
  completedOnTime: number;
  completedLate: number;
  averageDelayDays: number;
  overdueTasks: number;
  completionRatePercent: number;
}

export interface PmDashboardDto {
  id: string;
  projectId?: string;
  name: string;
  isDefault: boolean;
  widgetCount: number;
  createdAt: string;
  isShared?: boolean;
}

export interface PmDashboardWidgetDto {
  id: string;
  title: string;
  widgetType: number;
  widgetTypeLabel: string;
  configJson?: string;
  positionX: number;
  positionY: number;
  width: number;
  height: number;
  sortOrder: number;
}

export interface PmDashboardDetailDto {
  id: string;
  projectId?: string;
  name: string;
  isDefault: boolean;
  widgets: PmDashboardWidgetDto[];
  isShared?: boolean;
}

export const PM_WIDGET_TYPES = [
  { value: 0, label: 'Summary' },
  { value: 1, label: 'Burndown' },
  { value: 2, label: 'Burnup' },
  { value: 3, label: 'Velocity' },
  { value: 4, label: 'Team Workload' },
  { value: 5, label: 'Saved Filter' },
  { value: 6, label: 'Deadline Dashboard' },
  { value: 7, label: 'Overdue Report' },
  { value: 8, label: 'Project Health' },
  { value: 9, label: 'Sprint Progress' },
  { value: 10, label: 'Open Issues' },
  { value: 11, label: 'Recent Activity' },
  { value: 12, label: 'Time Logged' },
  { value: 13, label: 'Issue Distribution' },
  { value: 14, label: 'Priority Distribution' },
  { value: 15, label: 'Risk Report' }
];

export interface ProjectDashboardDto {
  projectId: string;
  projectName?: string;
  health?: ProjectDeadlineHealthDto;
  healthLabel?: string;
  healthScore?: number;
  riskScore?: number;
  velocityTrend?: number | string;
  overduePercent?: number;
  blockedPercent?: number;
  completionPercent?: number;
  openIssues?: number;
  inProgressIssues?: number;
  completedIssues?: number;
  doneIssues?: number;
  blockedIssues?: number;
  overdueIssues?: number;
  unassignedIssues?: number;
  activeSprints?: number;
  velocityAverage?: number;
  sprintProgress?: {
    sprintId?: string;
    sprintName?: string;
    completedPoints?: number;
    totalPoints?: number;
    percentComplete?: number;
  };
  velocity?: VelocityReportDto;
  upcomingDeadlines?: DeadlineReportRowDto[];
  recentActivity?: {
    message: string;
    occurredAt: string;
    actorName?: string;
    activityTypeLabel?: string;
  }[];
}

export interface WidgetDataDto {
  widgetType?: number;
  title?: string;
  summary?: PmSummaryDto;
  burndown?: BurndownReportDto;
  burnup?: BurnupReportDto;
  velocity?: VelocityReportDto;
  workload?: TeamWorkloadDto[];
  deadlineDashboard?: DeadlineDashboardDto;
  projectHealth?: ProjectDeadlineHealthDto;
  overdueRows?: DeadlineReportRowDto[];
  chartPoints?: ChartPointDto[];
  tableRows?: Record<string, unknown>[];
  listItems?: { key?: string; summary?: string; message?: string; label?: string; count?: number; percentComplete?: number; hours?: number; name?: string }[];
  sprintProgress?: { sprintName?: string; completedPoints?: number; totalPoints?: number; percentComplete?: number };
  timeLogged?: { totalHours?: number; items?: { name?: string; hours?: number }[] };
  riskReport?: { high?: number; medium?: number; low?: number; items?: { key?: string; summary?: string; risk?: string }[] };
  raw?: unknown;
}

export interface DistributionReportDto {
  projectId?: string;
  total?: number;
  items: { label: string; count: number; percent?: number }[];
}

export interface SprintReportDto {
  sprintId: string;
  sprintName: string;
  committedPoints?: number;
  completedPoints?: number;
  completionPercent?: number;
  burndown?: BurndownReportDto;
  items?: ChartPointDto[];
}

export interface ProgressReportDto {
  completionPercent?: number;
  byStatus?: { label: string; count: number }[];
  byType?: { label: string; count: number }[];
}

export interface TrendReportDto {
  points: ChartPointDto[];
  total?: number;
  openBugs?: number;
  closedBugs?: number;
  dailyTrend?: { date: string; opened: number; closed: number }[];
}

export interface ThroughputWeekDto {
  weekStart: string;
  completedIssues: number;
  completedPoints: number;
}

export interface ThroughputReportDto {
  projectId: string;
  weeks: number;
  weekly: ThroughputWeekDto[];
}

export interface TimeUtilizationReportDto {
  projectId?: string;
  totalHours: number;
  byEmployee: { employeeId?: string; employeeName: string; totalHours: number }[];
  byIssue: { issueId?: string; issueKey?: string; issueSummary?: string; totalHours: number }[];
  /** @deprecated legacy alias */
  items?: { label: string; hours: number; percent?: number }[];
}

export interface ResourceUtilizationReportDto {
  projectId?: string;
  assignees: {
    employeeId?: string;
    employeeName: string;
    openIssues: number;
    totalStoryPoints?: number;
    remainingStoryPoints: number;
    leaveDays?: number;
    availableHours: number;
    isOverallocated: boolean;
  }[];
  /** @deprecated legacy alias */
  items?: {
    employeeName: string;
    allocatedHours?: number;
    loggedHours?: number;
    utilizationPercent?: number;
  }[];
}

export interface RiskReportDto {
  projectId?: string;
  highRiskIssues?: {
    issueId?: string;
    key?: string;
    keyOrTitle?: string;
    summary?: string;
    riskLevel?: string | number;
    riskLevelLabel?: string;
    statusLabel?: string;
    dueDate?: string;
  }[];
  overdueIssues?: {
    issueId?: string;
    key?: string;
    keyOrTitle?: string;
    summary?: string;
    riskLevel?: string | number;
    statusLabel?: string;
    dueDate?: string;
  }[];
  /** @deprecated legacy */
  total?: number;
  items?: {
    keyOrTitle: string;
    riskLevel?: string;
    assigneeName?: string;
    dueDate?: string;
    statusLabel?: string;
  }[];
}

export interface ProjectSlaDto {
  projectId: string;
  onTimePercent?: number;
  overduePercent?: number;
  averageResolutionDays?: number;
  slaTargetPercent?: number;
  metSla?: boolean;
  healthLabel?: string;
  breachedCount?: number;
  atRiskCount?: number;
}

export interface CumulativeFlowDayDto {
  date: string;
  toDo: number;
  inProgress: number;
  done: number;
  /** @deprecated legacy */
  statuses?: { label: string; count: number }[];
}

export interface CumulativeFlowReportDto {
  projectId?: string;
  from?: string;
  to?: string;
  points: CumulativeFlowDayDto[];
  /** @deprecated legacy */
  days?: CumulativeFlowDayDto[];
  statusLabels?: string[];
}

export interface CycleTimeBucketDto {
  label: string;
  count: number;
  percent?: number;
}

export interface CycleTimeReportDto {
  projectId?: string;
  from?: string;
  to?: string;
  averageDays?: number;
  medianDays?: number;
  distribution: CycleTimeBucketDto[];
}

export interface ForecastReportDto {
  projectId?: string;
  remainingPoints?: number;
  averageVelocity?: number;
  estimatedCompletionDate?: string;
  sprintsRemaining?: number;
  confidenceLabel?: string;
}

export interface BlockedIssueRowDto {
  id: string;
  keyOrTitle: string;
  statusLabel?: string;
  assigneeName?: string;
  blockedSince?: string;
  blockedReason?: string;
  dueDate?: string;
}

export interface MilestoneProgressRowDto {
  id: string;
  name: string;
  targetDate?: string;
  startDate?: string;
  releaseDate?: string;
  status?: string;
  statusLabel?: string;
  completionPercent: number;
  issueCount?: number;
  completedCount?: number;
  totalCount?: number;
  versionName?: string;
}

export interface MilestoneProgressReportDto {
  projectId?: string;
  milestones: MilestoneProgressRowDto[];
}

export interface ReleaseProgressReportDto {
  projectId?: string;
  releases: MilestoneProgressRowDto[];
}

export interface ProjectCapacityDto {
  projectId: string;
  from: string;
  to: string;
  sprintCapacityPoints: number;
  assignedOpenPoints: number;
  assigneeLoad: ResourceUtilizationReportDto['assignees'];
  workingDays?: number;
  holidayDays?: number;
  leaveDays?: number;
  availableHours?: number;
  overallocatedAssigneeCount?: number;
}

export interface SprintHealthReportDto {
  sprintId: string;
  sprintName: string;
  health: string;
  healthLabel: string;
  totalIssues: number;
  completedIssues: number;
  overdueIssues: number;
  blockedIssues: number;
  completionPercent: number;
  committedPoints: number;
  completedPoints: number;
  sprintDelayDays: number;
  startDate: string;
  endDate: string;
}

export interface EmployeeProductivityDto {
  employeeId?: string;
  employeeName: string;
  issuesCompleted: number;
  issuesOpen: number;
  storyPointsCompleted: number;
  hoursLogged: number;
  completionRatePercent: number;
  averageDelayDays: number;
  overdueTasks: number;
}

export interface ProductivityReportDto {
  projectId: string;
  employees: EmployeeProductivityDto[];
  teamAverageCompletionRate: number;
  teamAverageVelocityContribution: number;
}
